import { camelizeFieldKey } from "@/utils/fieldKeys";

export type ActionButtonUrlResult =
  | { ok: true; url: string }
  | { ok: false; reason: "missing_value" | "invalid_url" };

const PLACEHOLDER = /\{\{\s*([^{}\s]+)\s*\}\}/g;

// Field keys are configured in their API (snake_case) form, while the record
// the button renders on is camelized — accept either spelling. "." and ".."
// survive encoding and would be resolved as path segments by URL parsing.
const resolveValue = (
  record: Record<string, unknown>,
  key: string
): string | null => {
  const value = record[key] ?? record[camelizeFieldKey(key)];
  if (typeof value === "string") {
    return value === "" || value === "." || value === ".." ? null : value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return null;
};

/**
 * Fills `{{field_key}}` placeholders in a link template from the record and
 * returns the link only when it is an absolute https URL. Every value is
 * URI-component encoded, so a value can never add path segments, query
 * parameters or a fragment of its own. A placeholder with no usable value
 * fails rather than producing a link to the wrong page.
 *
 * The template is admin-configured and trusted as written. A placeholder in
 * the host (`https://{{domain}}/...`) is allowed and lets the record's value
 * choose the host, so anyone who can edit that field picks where the link goes.
 */
export const buildActionButtonUrl = (
  template: string,
  record: Record<string, unknown>
): ActionButtonUrlResult => {
  let missing = false;
  try {
    const filled = template.replace(PLACEHOLDER, (_, key: string) => {
      const value = resolveValue(record, key);
      if (value === null) {
        missing = true;
        return "";
      }
      return encodeURIComponent(value);
    });
    if (missing) return { ok: false, reason: "missing_value" };
    // Encoded values can't contain braces, so any left over are a placeholder
    // the pattern couldn't read (e.g. `{{owner name}}`).
    if (/[{}]/.test(filled)) return { ok: false, reason: "invalid_url" };

    const url = new URL(filled);
    return url.protocol === "https:"
      ? { ok: true, url: url.href }
      : { ok: false, reason: "invalid_url" };
  } catch {
    return { ok: false, reason: "invalid_url" };
  }
};
